import React, { useState, useEffect } from 'react';
import { Settings, User, Bell, Shield, Save } from 'lucide-react';
import { useSaasAuth } from '../../../context/SaasAuthContext';
import saasApiService from '../../../services/saasApi';

interface UserSettings {
  notifications: {
    email_alerts: boolean;
    sms_alerts: boolean;
    route_updates: boolean;
    maintenance_reminders: boolean;
  };
  preferences: {
    language: string;
    timezone: string;
    currency: string;
    date_format: string;
  };
  security: {
    two_factor_enabled: boolean;
    session_timeout: number;
  };
}

const SettingsManager: React.FC = () => {
  const { user } = useSaasAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'notifications' | 'preferences' | 'security'>('profile');
  const [settings, setSettings] = useState<UserSettings>({
    notifications: {
      email_alerts: true,
      sms_alerts: false,
      route_updates: true,
      maintenance_reminders: true
    },
    preferences: {
      language: 'es',
      timezone: 'America/Lima',
      currency: 'PEN',
      date_format: 'DD/MM/YYYY'
    },
    security: {
      two_factor_enabled: false,
      session_timeout: 30
    }
  });
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    company_name: user?.company_name || '',
    address: user?.address || ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await saasApiService.getUserSettings();
      if (response && response.settings) {
        // Mapear la respuesta del API a nuestro formato local
        const apiSettings = response.settings;
        setSettings({
          notifications: {
            email_alerts: apiSettings.notifications?.email_notifications || true,
            sms_alerts: apiSettings.notifications?.push_notifications || false,
            route_updates: apiSettings.notifications?.route_reminders || true,
            maintenance_reminders: apiSettings.notifications?.maintenance_alerts || true
          },
          preferences: {
            language: apiSettings.preferences?.language || 'es',
            timezone: apiSettings.preferences?.timezone || 'America/Lima',
            currency: apiSettings.preferences?.currency || 'PEN',
            date_format: apiSettings.preferences?.date_format || 'DD/MM/YYYY'
          },
          security: {
            two_factor_enabled: false,
            session_timeout: 30
          }
        });
        
        // Actualizar datos del perfil
        if (apiSettings.profile) {
          setProfileData({
            first_name: apiSettings.profile.first_name || '',
            last_name: apiSettings.profile.last_name || '',
            email: apiSettings.profile.email || '',
            phone: apiSettings.profile.phone || '',
            company_name: apiSettings.profile.company_name || '',
            address: ''
          });
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const saveProfile = async () => {
    try {
      setLoading(true);
      await saasApiService.updateProfile(profileData);
      alert('Perfil actualizado exitosamente');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error al actualizar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setLoading(true);
      await saasApiService.updateSettings(settings);
      alert('Configuración guardada exitosamente');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error al guardar la configuración');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Configuración</h1>
        <button
          onClick={activeTab === 'profile' ? saveProfile : saveSettings}
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center disabled:opacity-50"
        >
          <Save className="h-4 w-4 mr-2" />
          {loading ? 'Guardando...' : 'Guardar Cambios'}
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'profile', name: 'Perfil', icon: User },
            { id: 'notifications', name: 'Notificaciones', icon: Bell },
            { id: 'preferences', name: 'Preferencias', icon: Settings },
            { id: 'security', name: 'Seguridad', icon: Shield }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Información del Perfil
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre</label>
                <input
                  type="text"
                  value={profileData.first_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, first_name: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Apellido</label>
                <input
                  type="text"
                  value={profileData.last_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, last_name: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                <input
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Empresa</label>
                <input
                  type="text"
                  value={profileData.company_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, company_name: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Dirección</label>
                <textarea
                  value={profileData.address}
                  onChange={(e) => setProfileData(prev => ({ ...prev, address: e.target.value }))}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Configuración de Notificaciones
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Alertas por Email</h4>
                  <p className="text-sm text-gray-500">Recibir notificaciones importantes por correo electrónico</p>
                </div>
                <button
                  onClick={() => setSettings(prev => ({
                    ...prev,
                    notifications: { ...prev.notifications, email_alerts: !prev.notifications.email_alerts }
                  }))}
                  className={`${
                    settings.notifications.email_alerts ? 'bg-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                >
                  <span
                    className={`${
                      settings.notifications.email_alerts ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Alertas por SMS</h4>
                  <p className="text-sm text-gray-500">Recibir notificaciones urgentes por mensaje de texto</p>
                </div>
                <button
                  onClick={() => setSettings(prev => ({
                    ...prev,
                    notifications: { ...prev.notifications, sms_alerts: !prev.notifications.sms_alerts }
                  }))}
                  className={`${
                    settings.notifications.sms_alerts ? 'bg-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                >
                  <span
                    className={`${
                      settings.notifications.sms_alerts ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Actualizaciones de Rutas</h4>
                  <p className="text-sm text-gray-500">Notificaciones sobre cambios en las rutas</p>
                </div>
                <button
                  onClick={() => setSettings(prev => ({
                    ...prev,
                    notifications: { ...prev.notifications, route_updates: !prev.notifications.route_updates }
                  }))}
                  className={`${
                    settings.notifications.route_updates ? 'bg-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                >
                  <span
                    className={`${
                      settings.notifications.route_updates ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Recordatorios de Mantenimiento</h4>
                  <p className="text-sm text-gray-500">Alertas sobre mantenimiento de vehículos</p>
                </div>
                <button
                  onClick={() => setSettings(prev => ({
                    ...prev,
                    notifications: { ...prev.notifications, maintenance_reminders: !prev.notifications.maintenance_reminders }
                  }))}
                  className={`${
                    settings.notifications.maintenance_reminders ? 'bg-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                >
                  <span
                    className={`${
                      settings.notifications.maintenance_reminders ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Tab */}
      {activeTab === 'preferences' && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Preferencias del Sistema
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Idioma</label>
                <select
                  value={settings.preferences.language}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    preferences: { ...prev.preferences, language: e.target.value }
                  }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="es">Español</option>
                  <option value="en">English</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Zona Horaria</label>
                <select
                  value={settings.preferences.timezone}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    preferences: { ...prev.preferences, timezone: e.target.value }
                  }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="America/Lima">Lima (UTC-5)</option>
                  <option value="America/New_York">New York (UTC-5)</option>
                  <option value="Europe/Madrid">Madrid (UTC+1)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Moneda</label>
                <select
                  value={settings.preferences.currency}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    preferences: { ...prev.preferences, currency: e.target.value }
                  }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="PEN">Soles Peruanos (PEN)</option>
                  <option value="USD">Dólares Americanos (USD)</option>
                  <option value="EUR">Euros (EUR)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Formato de Fecha</label>
                <select
                  value={settings.preferences.date_format}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    preferences: { ...prev.preferences, date_format: e.target.value }
                  }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Configuración de Seguridad
            </h3>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Autenticación de Dos Factores</h4>
                  <p className="text-sm text-gray-500">Agregar una capa extra de seguridad a tu cuenta</p>
                </div>
                <button
                  onClick={() => setSettings(prev => ({
                    ...prev,
                    security: { ...prev.security, two_factor_enabled: !prev.security.two_factor_enabled }
                  }))}
                  className={`${
                    settings.security.two_factor_enabled ? 'bg-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                >
                  <span
                    className={`${
                      settings.security.two_factor_enabled ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Tiempo de Sesión (minutos)</label>
                <select
                  value={settings.security.session_timeout}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    security: { ...prev.security, session_timeout: parseInt(e.target.value) }
                  }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value={15}>15 minutos</option>
                  <option value={30}>30 minutos</option>
                  <option value={60}>1 hora</option>
                  <option value={120}>2 horas</option>
                </select>
              </div>

              <div className="border-t pt-4">
                <button className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">
                  Cambiar Contraseña
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsManager;
